
import Control from 'react-leaflet-custom-control'
import { Search as SearchIcon, MeetingRoom as RoomIcon, Adjust as AdjustIcon, LocalLibrary as LocalLibraryIcon, Architecture as ArchitectureIcon, Person as PersonIcon, AdminPanelSettings as AdminIcon, MyLocation as MyLocationIcon, Layers as LayersIcon, Menu as MenuIcon, Close as CloseIcon, Settings as SettingsIcon, Build as BuildIcon } from '@mui/icons-material'
import { Button, Switch, ButtonGroup, FormControlLabel, Drawer, Box, IconButton, Typography, Divider } from '@mui/material'
import { useState } from "react";
import { useMap } from 'react-leaflet/hooks'
import './i18n/config';
import { useTranslation, Trans } from 'react-i18next';
import { LanguageSelector } from "./LanguageSelector.js";
import Circle from '@uiw/react-color-circle';
import { useIsMobile } from './hooks/useIsMobile.js';

function HiddenLabelButton(props: any) {

    let { children, label, showLabel, ...rest } = props;
    return (
        <Button
            {...rest} style={{ justifyContent: "flex-start" }}
        >
            {children} <wbr />
            {showLabel && label}
        </Button>
    );
}

function LabeledSwitch(props: any) {
    let { label, showLabel, ...rest } = props;

    // When we do not have a label
    // we do not wan the label to take any space
    let sx = {}
    if (!showLabel) {
        sx['marginRight'] = 0;
    }

    return (
        <FormControlLabel sx={sx} label={showLabel && label} control={<Switch {...rest}></Switch>} />
    )
}

export function FindCanopyButton(props: any) {
    const { t } = useTranslation();
    const { showLabel = true, onClick = () => { }, ...rest } = props;
    return (
        <HiddenLabelButton variant="contained" color='inherit' label={t("Find canopy")} showLabel={showLabel} onClick={onClick} {...rest}>
            <SearchIcon />
        </HiddenLabelButton>
    )
}

export function CanopyButtons(props: any) {
    const { showColors = false, isAdmin = false, onShowEditControls = () => { }, onShowCanopySearch = () => { }, onShowRoom = () => { }, onShowUsername = () => { }, onShowAdmin = () => { }, onSelectColor = (hex) => { }, onStartTutorial = () => { }, mobileMenuOpen: externalMobileMenuOpen, setMobileMenuOpen: externalSetMobileMenuOpen } = props;
    const [buttonsHovered, setButtonsHovered] = useState(false);
    const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);
    
    // Use external state if provided, otherwise use internal state
    const mobileMenuOpen = externalMobileMenuOpen !== undefined ? externalMobileMenuOpen : internalMobileMenuOpen;
    const setMobileMenuOpen = externalSetMobileMenuOpen || setInternalMobileMenuOpen;
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    const buttonData = [
        { icon: <LocalLibraryIcon />, label: t("Tutorial"), onClick: onStartTutorial },
        { icon: <PersonIcon />, label: t("Username"), onClick: onShowUsername },
        { icon: <RoomIcon />, label: t("Set room"), onClick: onShowRoom },
        ...(isAdmin ? [{ icon: <AdminIcon />, label: t("Admin"), onClick: onShowAdmin, color: 'error' as const }] : []),
        { icon: <SearchIcon />, label: t("Find canopy"), onClick: onShowCanopySearch },
        { icon: <ArchitectureIcon />, label: t("Show edit controls"), onClick: onShowEditControls }
    ];

    const handleMobileButtonClick = (onClick: () => void) => {
        onClick();
        setMobileMenuOpen(false);
    };

    if (isMobile) {
        return (
            <>
                <Control position='topleft' prepend={true}>
                    {showColors && <Circle colors={['#d90000', '#eef205', '#0df205', '#0560f2']}
                        onChange={(color) => {
                            onSelectColor(color.hex);
                        }}
                    />}
                </Control>
                <Control position='topleft' prepend={true}>
                    <IconButton
                        className="mobile-menu-toggle"
                        onClick={() => setMobileMenuOpen(true)}
                        sx={{
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: '#f5f5f5' },
                            boxShadow: 1
                        }}
                    >
                        <SettingsIcon />
                    </IconButton>
                </Control>
                
                <Drawer
                    anchor="bottom"
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            maxHeight: '60vh'
                        }
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">{t("Navigation")}</Typography>
                            <IconButton onClick={() => setMobileMenuOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                            {buttonData.map((button, index) => (
                                <Button
                                    key={index}
                                    variant="contained"
                                    color={button.color || 'inherit'}
                                    startIcon={button.icon}
                                    onClick={() => handleMobileButtonClick(button.onClick)}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        minHeight: 48,
                                        textAlign: 'left'
                                    }}
                                >
                                    {button.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                </Drawer>
            </>
        );
    }

    return (
        <>
            <Control position='topleft' prepend={true}>
                {showColors && <Circle colors={['#d90000', '#eef205', '#0df205', '#0560f2']}
                    onChange={(color) => {
                        onSelectColor(color.hex);
                    }}
                />}
            </Control>
            <Control position='topleft' prepend={true}>
                <div id='canopy-buttons-div'>
                    <ButtonGroup orientation="vertical"
                        onMouseOver={() => setButtonsHovered(true)}
                        onMouseOut={() => setButtonsHovered(false)}>
                        <HiddenLabelButton variant="contained" color='inherit' label={t("Tutorial")} showLabel={buttonsHovered} onClick={onStartTutorial}>
                            <LocalLibraryIcon />
                        </HiddenLabelButton>
                        <HiddenLabelButton variant="contained" color='inherit' label={t("Username")} showLabel={buttonsHovered} onClick={onShowUsername}>
                            <PersonIcon />
                        </HiddenLabelButton>
                        <HiddenLabelButton variant="contained" color='inherit' label={t("Set room")} showLabel={buttonsHovered} onClick={onShowRoom}>
                            <RoomIcon />
                        </HiddenLabelButton>
                        {isAdmin && (
                            <HiddenLabelButton variant="contained" color='error' label={t("Admin")} showLabel={buttonsHovered} onClick={onShowAdmin}>
                                <AdminIcon />
                            </HiddenLabelButton>
                        )}
                        <FindCanopyButton id="find-canopy-button" showLabel={buttonsHovered} onClick={onShowCanopySearch}></FindCanopyButton>
                        <HiddenLabelButton variant="contained" color='inherit' label={t("Show edit controls")} showLabel={buttonsHovered} onClick={onShowEditControls}>
                            <ArchitectureIcon />
                        </HiddenLabelButton>
                    </ButtonGroup>
                </div>
            </Control>
        </>
    )
}

export function AdditionalButtons(props: any) {
    const { releaseLocation = null, selectedLayer = 'OSM', onLayerChange = () => {}, locationWatching = false, onLocationTrackingToggle = () => {}, mobileMenuOpen: externalMobileMenuOpen, setMobileMenuOpen: externalSetMobileMenuOpen, ...rest } = props;
    const [buttonsHovered, setButtonsHovered] = useState(false);
    const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);
    
    // Use external state if provided, otherwise use internal state
    const mobileMenuOpen = externalMobileMenuOpen !== undefined ? externalMobileMenuOpen : internalMobileMenuOpen;
    const setMobileMenuOpen = externalSetMobileMenuOpen || setInternalMobileMenuOpen;
    const { t, i18n } = useTranslation();
    const map = useMap();
    const isMobile = useIsMobile();
    
    const onCenterToReleaseLocation = () => {
        if (releaseLocation) {
            map.flyTo(releaseLocation);
        }
    }
    
    const layers = ['OSM', 'Google Satellite', 'Esri Satellite', 'Peruskartta'];
    const onToggleLayer = () => {
        const currentIndex = layers.indexOf(selectedLayer);
        const nextIndex = (currentIndex + 1) % layers.length;
        onLayerChange(layers[nextIndex]);
    }
    
    const toggleLanguage = () => {
        i18n.changeLanguage(i18n.language === 'en' ? 'fi' : 'en');
    }

    const buttonData = [
        { icon: <LayersIcon />, label: selectedLayer, onClick: onToggleLayer },
        { icon: <MyLocationIcon />, label: t("Track location"), onClick: onLocationTrackingToggle, color: locationWatching ? 'primary' as const : 'inherit' as const },
        { icon: <AdjustIcon />, label: t("Pan to center"), onClick: onCenterToReleaseLocation },
        { icon: <LanguageSelector />, label: t("Language"), onClick: toggleLanguage }
    ];

    const handleMobileButtonClick = (onClick: () => void) => {
        onClick();
        setMobileMenuOpen(false);
    };

    if (isMobile) {
        return (
            <>
                <Control position='topright'>
                    <IconButton
                        className="mobile-menu-toggle-additional"
                        onClick={() => setMobileMenuOpen(true)}
                        sx={{
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: '#f5f5f5' },
                            boxShadow: 1
                        }}
                    >
                        <BuildIcon />
                    </IconButton>
                </Control>
                
                <Drawer
                    anchor="bottom"
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            maxHeight: '60vh'
                        }
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">{t("Tools")}</Typography>
                            <IconButton onClick={() => setMobileMenuOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                            {buttonData.map((button, index) => (
                                <Button
                                    key={index}
                                    id={index === 0 ? "map-layer-button" : undefined}
                                    variant="contained"
                                    color={button.color || 'inherit'}
                                    startIcon={button.icon}
                                    onClick={() => handleMobileButtonClick(button.onClick)}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        minHeight: 48,
                                        textAlign: 'left'
                                    }}
                                >
                                    {button.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                </Drawer>
            </>
        );
    }

    return (
        <Control position='topright'>
            <div id='additional-buttons-div'>
                <ButtonGroup orientation="vertical"
                    onMouseOver={() => setButtonsHovered(true)}
                    onMouseOut={() => setButtonsHovered(false)}>
                    <HiddenLabelButton id="map-layer-button" variant="contained" color='inherit' label={selectedLayer} showLabel={buttonsHovered} onClick={onToggleLayer}>
                        <LayersIcon />
                    </HiddenLabelButton>
                    <HiddenLabelButton variant="contained" color={locationWatching ? 'primary' : 'inherit'} label={t("Track location")} showLabel={buttonsHovered} onClick={onLocationTrackingToggle}>
                        <MyLocationIcon />
                    </HiddenLabelButton>
                    <HiddenLabelButton variant="contained" color='inherit' label={t("Pan to center")} showLabel={buttonsHovered} onClick={onCenterToReleaseLocation}>
                        <AdjustIcon />
                    </HiddenLabelButton>
                    <HiddenLabelButton variant="contained" color='inherit' label={" - " + t("Language")} showLabel={buttonsHovered} onClick={toggleLanguage}>
                        <LanguageSelector></LanguageSelector>
                    </HiddenLabelButton>
                </ButtonGroup>
            </div>
        </Control>
    )
}